/**
 * 검사 시점의 시스템 현황 — 성적서에 **찍히는 순간의 사실**을 적는다.
 *
 * 검사표 항목이 "사람이 본 것" 이라면 이쪽은 "서버가 그때 뭐라고 답했는가" 다. 나중에
 * 분쟁이 생겼을 때 판정보다 이 숫자들이 먼저 쓸모 있다 — 인도 시점에 몇 대가 붙어
 * 있었고 어떤 버전이었는지는 기억으로 복원되지 않는다.
 */
import type { Display, FleetSummary, MetricsReport, ReadyReport } from '../api/client.js';
import { displayLive } from '../components/displayLive.js';

/**
 * 검사 화면과 성적서가 함께 보는 서버 응답.
 *
 * 둘이 따로 불러오면 편집 화면에서 본 숫자와 인쇄된 숫자가 달라질 수 있다. 화면이 한 번
 * 불러와 양쪽에 넘긴다.
 */
export interface SystemSnapshot {
  ready: ReadyReport | null;
  readyError: string | null;
  metrics: MetricsReport | null;
  fleet: FleetSummary | null;
  displays: readonly Display[];
  displaysLoaded: boolean;
  displaysError: string | null;
  /** 검사를 수행한 계정 — 이메일, 없으면 주체 id. */
  account: string;
}

/** 서버 준비 상태 한 줄 — "정상 · DB ok · 서비스 켜짐". */
export function readyLabel(ready: ReadyReport | null, error: string | null): string {
  if (!ready) return error ?? '확인 중…';
  return `${ready.status === 'ok' ? '정상' : '저하됨'} · DB ${ready.checks.database} · 서비스 ${
    ready.checks.service_enabled ? '켜짐' : '꺼짐'
  }`;
}

/** 신원 발급자 목록. 비었으면 비었다고 말한다 — 빈칸은 "안 봤다" 로 읽힌다. */
export function issuersLabel(ready: ReadyReport | null): string {
  if (!ready) return '—';
  return ready.checks.identity_issuers.length > 0 ? ready.checks.identity_issuers.join(', ') : '등록된 호스트 발급자 없음';
}

/** 등록 화면 집계 한 줄. */
export function fleetLabel(fleet: FleetSummary | null): string {
  if (!fleet) return '—';
  return `${fleet.total}대 · 온라인 ${fleet.online} · 오프라인 ${fleet.offline} · 오류 ${fleet.erroring} · 정비 ${fleet.maintenance} · 숨김 ${fleet.hidden}`;
}

/** 출력 일시 — "2026-09-17 15:04", 로컬 기준. */
export function printedAt(now: Date = new Date()): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/** 에이전트 버전 분포 — "0.2.0 12대 · 미보고 1대". 한 대만 낮아도 눈에 띄게 한 줄로 만든다. */
export function versionSpread(displays: readonly Display[]): string {
  if (displays.length === 0) return '—';

  const counts = new Map<string, number>();
  for (const display of displays) {
    const version = display.status.app_version ?? '미보고';
    counts.set(version, (counts.get(version) ?? 0) + 1);
  }

  return [...counts.entries()]
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .map(([version, count]) => `${version} ${count}대`)
    .join(' · ');
}

/** 화면 한 대의 해상도 표기. 보고한 적이 없으면 빈칸으로 둔다 — 인쇄물에서 손으로 적는다. */
export function resolutionOf(display: Display): string {
  const { screen_w: width, screen_h: height } = display.status;
  if (!width || !height) return '';
  return `${width}×${height}`;
}

/**
 * 화면 상태 한 마디 — 성적서의 기기 목록 열. 나쁜 쪽을 먼저 말한다.
 *
 * 끊긴 화면은 "오프라인" 하나다(2026-10-21) — 정비 모드·오류는 마지막 보고에 남은 값이라 지금 상태가 아니다(`displayLive`).
 */
export function stateOf(display: Display): string {
  if (!display.online) return '오프라인';
  const live = displayLive(display);
  if (live.maintenance) return '정비 모드';
  if (live.error) return '오류 보고';
  return '정상';
}
